import { IncomeType } from '@zg-rentals/ts-capps-client';
import type { ApplicationConfigurationRequiredDocumentType } from '@zg-rentals/ts-capps-client';
import type { Answers, CurrentIncome, Document, RequiredDocumentSection } from '../models/rental-application';
import {
  APPLICATION_LEVEL_DOCUMENT_TYPES,
  INCOME_DOCUMENT_TYPE_ORDER,
  INCOME_LEVEL_DOCUMENT_TYPES,
  UNNAMED_INCOME_SOURCE,
} from '../constants/incomeDocs';
import type { IncomeDocumentSection } from '../hooks/useRequiredDocumentSections';
import { isIncomeDocumentSection, isRequiredSection } from '../hooks/useRequiredDocumentSections';

export type IncomeDocumentsMode = 'default' | 'template';

export type IncomeDocumentsAnswers = Pick<Answers, 'currentIncomes' | 'requiredDocuments'>;

export type RequestedRow = {
  documentType: ApplicationConfigurationRequiredDocumentType;
  section: RequiredDocumentSection;
  documents: Array<Document>;
};

export type IncomeSourceGroup = {
  income: CurrentIncome;
  index: number;
  label: string;
  requestedRows: Array<RequestedRow>;
  supportingDocuments: Array<Document>;
};

export type IncomeDocumentsModel = {
  mode: IncomeDocumentsMode;
  incomeSources: Array<IncomeSourceGroup>;
  applicationRows: Array<RequestedRow>;
  noLongerRequested: Array<RequestedRow>;
  requestedCount: number;
  coveredCount: number;
};

export type PendingRemoval =
  | {
      kind: 'detach';
      documentId: string;
      documentType: ApplicationConfigurationRequiredDocumentType;
      incomeId?: string;
    }
  | { kind: 'delete'; documentId: string };

type IndexedIncome = { income: CurrentIncome; index: number };

export function getIncomeSourceLabel(income: CurrentIncome, index: number) {
  const organization = income.type === IncomeType.EMPLOYMENT ? income.employer?.organization?.trim() : undefined;

  return organization || income.title?.trim() || `${UNNAMED_INCOME_SOURCE} #${index + 1}`;
}

function getDocuments(documents: Array<Document | null> | null | undefined): Array<Document> {
  return (documents ?? []).filter((document): document is Document => !!document);
}

function toRow(section: IncomeDocumentSection): RequestedRow {
  return { documentType: section.documentType, section, documents: getDocuments(section.documents) };
}

function getIncomes(answers: IncomeDocumentsAnswers | null | undefined): Array<IndexedIncome> {
  return (answers?.currentIncomes ?? []).flatMap((income, index) => (income ? [{ income, index }] : []));
}

function getSections(answers: IncomeDocumentsAnswers | null | undefined) {
  return (answers?.requiredDocuments ?? []).filter(isIncomeDocumentSection);
}

function getVisibleRows(incomes: Array<IndexedIncome>, sections: Array<IncomeDocumentSection>) {
  const requested = sections.filter(isRequiredSection);
  const findRow = (documentType: ApplicationConfigurationRequiredDocumentType, incomeId: string | undefined) => {
    const section = requested.find((s) => s.documentType === documentType && (s.incomeId || undefined) === incomeId);
    return section ? [toRow(section)] : [];
  };

  const incomeRows = new Map<number, Array<RequestedRow>>();
  for (const { income, index } of incomes) {
    if (income.id && income.type) {
      const types = INCOME_LEVEL_DOCUMENT_TYPES[income.type] ?? [];
      incomeRows.set(
        index,
        types.flatMap((documentType) => findRow(documentType, income.id!)),
      );
    }
  }
  const applicationRows = APPLICATION_LEVEL_DOCUMENT_TYPES.flatMap((documentType) => findRow(documentType, undefined));

  return { incomeRows, applicationRows };
}

function isHiddenBy(removal: PendingRemoval, documentId: string | null | undefined, section?: RequiredDocumentSection) {
  if (!documentId || documentId !== removal.documentId) {
    return false;
  }

  return (
    removal.kind === 'delete' ||
    (section?.documentType === removal.documentType && (section.incomeId || undefined) === removal.incomeId)
  );
}

export function withoutPendingRemovals(
  answers: IncomeDocumentsAnswers | null | undefined,
  removals: Array<PendingRemoval>,
): IncomeDocumentsAnswers | null | undefined {
  if (!answers || removals.length === 0) {
    return answers;
  }
  const isShown = (document: Document | null, section?: RequiredDocumentSection) =>
    !removals.some((removal) => isHiddenBy(removal, document?.id, section));

  return {
    currentIncomes: answers.currentIncomes?.map(
      (income) => income && { ...income, documents: income.documents?.filter((document) => isShown(document)) },
    ),
    requiredDocuments: answers.requiredDocuments?.map((section) => ({
      ...section,
      documents: section.documents?.filter((document) => isShown(document, section)),
    })),
  };
}

export function isPendingRemovalListed(answers: IncomeDocumentsAnswers | null | undefined, removal: PendingRemoval) {
  return (
    (answers?.requiredDocuments ?? []).some((section) =>
      (section.documents ?? []).some((document) => isHiddenBy(removal, document.id, section)),
    ) ||
    (answers?.currentIncomes ?? []).some((income) =>
      (income?.documents ?? []).some((document) => isHiddenBy(removal, document?.id)),
    )
  );
}

export function hasVisibleRequestedRow(answers: IncomeDocumentsAnswers | null | undefined) {
  const { incomeRows, applicationRows } = getVisibleRows(getIncomes(answers), getSections(answers));

  return applicationRows.length > 0 || [...incomeRows.values()].some((rows) => rows.length > 0);
}

export function getIncomeDocumentsModel(
  answers: IncomeDocumentsAnswers | null | undefined,
  { isTemplateEligible }: { isTemplateEligible: boolean },
): IncomeDocumentsModel {
  const incomes = getIncomes(answers);
  const sections = getSections(answers);
  const { incomeRows, applicationRows } = getVisibleRows(incomes, sections);
  const visibleSections = new Set([...[...incomeRows.values()].flat(), ...applicationRows].map((row) => row.section));
  const isTemplate = isTemplateEligible && visibleSections.size > 0;

  const incomeIndexById = new Map(incomes.flatMap(({ income, index }) => (income.id ? [[income.id, index]] : [])));
  const incomeOrder = (row: RequestedRow) => incomeIndexById.get(row.section.incomeId ?? '') ?? Number.MAX_SAFE_INTEGER;
  const noLongerRequested = sections
    .filter((section) => !visibleSections.has(section) && getDocuments(section.documents).length > 0)
    .map(toRow)
    .sort(
      (a, b) =>
        incomeOrder(a) - incomeOrder(b) ||
        INCOME_DOCUMENT_TYPE_ORDER.indexOf(a.documentType) - INCOME_DOCUMENT_TYPE_ORDER.indexOf(b.documentType),
    );

  const incomeSources = incomes.map(({ income, index }) => {
    const requestedRows = isTemplate ? (incomeRows.get(index) ?? []) : [];
    const rowDocumentIds = new Set(requestedRows.flatMap((row) => row.documents.map((document) => document.id)));

    return {
      income,
      index,
      label: getIncomeSourceLabel(income, index),
      requestedRows,
      supportingDocuments: getDocuments(income.documents).filter(
        (document) => !document.id || !rowDocumentIds.has(document.id),
      ),
    };
  });

  const rows = isTemplate ? [...incomeSources.flatMap((group) => group.requestedRows), ...applicationRows] : [];

  return {
    mode: isTemplate ? 'template' : 'default',
    incomeSources,
    applicationRows: isTemplate ? applicationRows : [],
    noLongerRequested,
    requestedCount: rows.length,
    coveredCount: rows.filter((row) => row.documents.length > 0).length,
  };
}
